/**
 * Arquivo de Configuração Principal - White Label
 * 
 * Este arquivo centraliza TODAS as configurações do projeto em um único lugar.
 * Para criar um novo tema/cliente, importe este arquivo e personalize conforme necessário.
 * 
 * Uso:
 * import config from './data/config';
 * 
 * Acesso:
 * config.colors.primary.main
 * config.texts.company.name
 * config.images.logos.main
 * config.links.social.whatsapp.url
 * config.content.ofertas
 */

import colors, { getColor, getCSSVariables } from './colors';
import texts, { getText, interpolate } from './texts';
import images, { getImage, getImageUrl } from './images';
import links, { getLink, formatPhone } from './links';
import content, { getOfertas, getFAQByCategory, searchFAQ } from './content';

// ============ CONFIGURAÇÃO PRINCIPAL ============
const config = {
  // Informações básicas do cliente/tenant
  client: {
    id: 'onitelecom',
    name: 'Oni Telecom',
    slug: 'oni',
    active: true,
    createdAt: '2020-01-01',
  },

  // Todas as cores do projeto
  colors,

  // Todos os textos do projeto
  texts,

  // Informações da empresa (atalho para texts.company)
  company: texts.company,

  // Todas as imagens do projeto
  images,

  // Todos os links do projeto
  links,

  // Todo o conteúdo dinâmico
  content,

  // ============ CONFIGURAÇÕES ADICIONAIS ============
  
  // SEO e Metadata
  seo: {
    title: 'Oni Telecom - Internet Fibra Óptica',
    description: 'Internet de alta velocidade com fibra óptica, telefonia e entretenimento. Planos a partir de R$ 99,90/mês.',
    keywords: 'internet fibra óptica, internet rápida, planos de internet, oni telecom, João Pessoa',
    author: 'Oni Telecom',
    ogImage: '/og-image.jpg',
    favicon: '/Oni.png',
    themeColor: '#8a1ce5',
  },

  // Configurações de API
  api: {
    baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  },

  // Configurações de Features (ligar/desligar funcionalidades)
  features: {
    carrinho: true,
    login: true,
    chat: true,
    newsletter: true,
    multiIdioma: false,
    darkMode: false,
    notificacoes: true,
    analytics: true,
  },

  // ============ CONFIGURAÇÃO DE PÁGINAS ============
  // Configure quais páginas existem e como devem ser montadas
  pages: {
    inicio: {
      enabled: true,
      path: '/',
      title: 'Oni Telecom - Internet Fibra Óptica',
      component: 'Inicio',
      layout: 'default', // default, minimal, full
      requireAuth: false,
      showInMenu: true,
      menuLabel: 'Início',
      menuOrder: 1,
      icon: 'home',
    },
    internet: {
      enabled: true,
      path: '/Internet',
      title: 'Planos de Internet - Oni Telecom',
      component: 'Internet',
      layout: 'default',
      requireAuth: false,
      showInMenu: true,
      menuLabel: 'Internet',
      menuOrder: 2,
      icon: 'wifi',
    },
    empresas: {
      enabled: true,
      path: '/ParaEmpresas',
      title: 'Soluções para Empresas - Oni Telecom',
      component: 'Empresas',
      layout: 'default',
      requireAuth: false,
      showInMenu: true,
      menuLabel: 'Empresas',
      menuOrder: 3,
      icon: 'building',
    },
    entretenimento: {
      enabled: true,
      path: '/Entretenimento',
      title: 'Entretenimento - Oni Telecom',
      component: 'Entretenimento',
      layout: 'default',
      requireAuth: false,
      showInMenu: true,
      menuLabel: 'Entretenimento',
      menuOrder: 4,
      icon: 'play',
    },
    planos: {
      enabled: true,
      path: '/Planos',
      title: 'Todos os Planos - Oni Telecom',
      component: 'Planos',
      layout: 'default',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'Planos',
      menuOrder: 5,
    },
    quemSomos: {
      enabled: true,
      path: '/Quemsomos',
      title: 'Quem Somos - Oni Telecom',
      component: 'Pagina2',
      layout: 'default',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'Quem Somos',
      menuOrder: 6,
    },
    ofertasChips: {
      enabled: true,
      path: '/OfertasChips',
      title: 'Ofertas de Chips - Oni Telecom',
      component: 'Pagina3',
      layout: 'default',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'Ofertas Chips',
      menuOrder: 7,
    },
    monteSeuPlano: {
      enabled: true,
      path: '/MonteSeuPlano',
      title: 'Monte seu Plano - Oni Telecom',
      component: 'MonteoSeu',
      layout: 'default',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'Monte seu Plano',
      menuOrder: 8,
    },
    faq: {
      enabled: true,
      path: '/perguntasfrequentes',
      title: 'Perguntas Frequentes - Oni Telecom',
      component: 'Perguntas',
      layout: 'default',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'FAQ',
      menuOrder: 9,
    },
    login: {
      enabled: true,
      path: '/Login',
      title: 'Login - Oni Telecom',
      component: 'Login',
      layout: 'minimal',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'Login',
      menuOrder: 10,
    },
    carrinho: {
      enabled: true,
      path: '/carrinho',
      title: 'Carrinho - Oni Telecom',
      component: 'Carrinho',
      layout: 'default',
      requireAuth: false,
      showInMenu: false,
      menuLabel: 'Carrinho',
      menuOrder: 11,
    },
  },

  // ============ CONTROLE DE COMPONENTES E ELEMENTOS ============
  // Configure quais componentes e elementos serão exibidos em cada página
  components: {
    // Página Inicial
    inicio: {
      carousel: true,
      servicosEssenciais: true,
      ofertas: true,
      servicosEssenciaisInternet: true,
      contato: true,
      infinitePossibilities: true,
      appExclusivo: true,
      planoControle: true,
      telefoniaBanner: true,
      helpSection: true,
      depoimentos: false, // Adicione quando quiser exibir
      faq: false,
      porQueEscolher: false,
    },
    
    // Componentes Globais
    global: {
      header: true,
      footer: true,
    },

    // Elementos dentro dos componentes
    elements: {
      header: {
        topBar: true,
        logo: true,
        navigation: true,
        searchBar: false,
        userMenu: true,
        cart: true,
      },
      footer: {
        logo: true,
        socialMedia: true,
        locationSelector: false,
        companyInfo: true,
        columns: true,
        appLinks: true,
        copyright: true,
      },
      ofertas: {
        headline: true,
        toggleAll: true,
        navigationButtons: true,
        seal: true,
        benefits: true,
        pricing: true,
        contractButton: true,
      },
      servicosEssenciais: {
        title: true,
        description: true,
        cards: true,
        icons: true,
      },
      contato: {
        form: true,
        map: false,
        phones: true,
        email: true,
        address: true,
      },
      helpSection: {
        title: true,
        description: true,
        buttons: true,
        backgroundImage: true,
      },
      faq: {
        search: true,
        categories: true,
        accordion: true,
      },
    },
  },

  // Configurações de Layout
  layout: {
    headerFixed: true,
    footerFixed: false,
    showBreadcrumbs: false,
    maxWidth: '1440px',
    containerPadding: '20px',
  },

  // Configurações de Formulários
  forms: {
    validation: {
      cpfRequired: true,
      phoneRequired: true,
      addressRequired: true,
    },
    masks: {
      cpf: '999.999.999-99',
      phone: '(99) 99999-9999',
      cep: '99999-999',
    },
  },

  // Configurações de E-commerce (se aplicável)
  ecommerce: {
    currency: 'BRL',
    locale: 'pt-BR',
    paymentMethods: ['credit_card', 'debit_card', 'pix', 'boleto'],
    shippingMethods: ['instalacao_gratis'],
  },

  // Analytics e Tracking
  analytics: {
    googleAnalyticsId: process.env.REACT_APP_GA_ID || '',
    facebookPixelId: process.env.REACT_APP_FB_PIXEL || '',
    hotjarId: process.env.REACT_APP_HOTJAR_ID || '',
  },

  // Integrações de terceiros
  integrations: {
    whatsapp: {
      enabled: true,
      number: '5583976010064',
      message: 'Olá! Gostaria de mais informações.',
    },
    chat: {
      enabled: false,
      provider: 'tawk', // tawk, zendesk, intercom, etc.
      id: '',
    },
    maps: {
      enabled: false,
      apiKey: process.env.REACT_APP_MAPS_KEY || '',
    },
  },

  // Configurações de Localização
  locale: {
    default: 'pt-BR',
    timezone: 'America/Sao_Paulo',
    currency: 'BRL',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: 'HH:mm',
  },
};

// ============ FUNÇÕES AUXILIARES GLOBAIS ============

/**
 * Obtém uma configuração por caminho
 * Ex: getConfig('colors.primary.main')
 */
export const getConfig = (path, defaultValue = null) => {
  const keys = path.split('.');
  let value = config;
  
  for (const key of keys) {
    value = value?.[key];
    if (value === undefined) {
      console.warn(`Configuração não encontrada: ${path}`);
      return defaultValue;
    }
  }
  
  return value;
};

/**
 * Verifica se uma feature está habilitada
 */
export const isFeatureEnabled = (featureName) => {
  return config.features[featureName] === true;
};

/**
 * Verifica se um componente deve ser exibido
 * Ex: shouldShowComponent('inicio', 'carousel')
 */
export const shouldShowComponent = (page, componentName) => {
  return config.components[page]?.[componentName] === true;
};

/**
 * Verifica se um elemento dentro de um componente deve ser exibido
 * Ex: shouldShowElement('header', 'topBar')
 */
export const shouldShowElement = (componentName, elementName) => {
  return config.components.elements[componentName]?.[elementName] === true;
};

/**
 * Obtém todos os componentes visíveis de uma página
 */
export const getVisibleComponents = (page) => {
  const pageComponents = config.components[page] || {};
  return Object.entries(pageComponents)
    .filter(([_, visible]) => visible === true)
    .map(([name, _]) => name);
};

/**
 * Obtém configuração de uma página específica
 */
export const getPageConfig = (pageKey) => {
  return config.pages[pageKey] || null;
};

/**
 * Obtém todas as páginas habilitadas
 */
export const getEnabledPages = () => {
  return Object.entries(config.pages)
    .filter(([_, pageConfig]) => pageConfig.enabled === true)
    .map(([key, pageConfig]) => ({ key, ...pageConfig }));
};

/**
 * Obtém páginas que devem aparecer no menu
 */
export const getMenuPages = () => {
  return getEnabledPages()
    .filter(page => page.showInMenu === true)
    .sort((a, b) => a.menuOrder - b.menuOrder);
};

/**
 * Obtém rotas para o React Router
 */
export const getRoutes = () => {
  return getEnabledPages().map(page => ({
    path: page.path,
    component: page.component,
    title: page.title,
    requireAuth: page.requireAuth,
    layout: page.layout,
  }));
};

/**
 * Verifica se uma página está habilitada
 */
export const isPageEnabled = (pageKey) => {
  return config.pages[pageKey]?.enabled === true;
};

/**
 * Formata valor monetário
 */
export const formatCurrency = (value) => {
  return new Intl.NumberFormat(config.locale.default, {
    style: 'currency',
    currency: config.locale.currency,
  }).format(value);
};

/**
 * Formata data
 */
export const formatDate = (date, format = config.locale.dateFormat) => {
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return format
    .replace('DD', day)
    .replace('MM', month)
    .replace('YYYY', year);
};

/**
 * Carrega configuração do cliente do backend
 * Esta função seria usada para carregar configs dinâmicas do banco de dados
 */
export const loadClientConfig = async (clientId) => {
  try {
    // Aqui você faria uma chamada à API para buscar as configurações do cliente
    // const response = await fetch(`${config.api.baseURL}/clients/${clientId}/config`);
    // const clientConfig = await response.json();
    
    // Mescla as configurações do cliente com as configurações padrão
    // return { ...config, ...clientConfig };
    
    console.log('loadClientConfig called for:', clientId);
    return config;
  } catch (error) {
    console.error('Erro ao carregar configuração do cliente:', error);
    return config;
  }
};

/**
 * Aplica tema personalizado (cores CSS)
 */
export const applyTheme = (customColors = null) => {
  const root = document.documentElement;
  const themeColors = customColors || colors;
  
  // Aplica cores primárias
  Object.entries(themeColors.primary).forEach(([key, value]) => {
    root.style.setProperty(`--color-primary-${key}`, value);
  });
  
  // Aplica cores secundárias
  Object.entries(themeColors.secondary).forEach(([key, value]) => {
    root.style.setProperty(`--color-secondary-${key}`, value);
  });
  
  // Aplica cores neutras
  Object.entries(themeColors.neutral).forEach(([key, value]) => {
    root.style.setProperty(`--color-neutral-${key}`, value);
  });
  
  console.log('Tema aplicado com sucesso!');
};

// Exporta tudo
export {
  colors,
  texts,
  images,
  links,
  content,
  getColor,
  getText,
  getImage,
  getLink,
  getOfertas,
  getFAQByCategory,
  searchFAQ,
  formatPhone,
  getCSSVariables,
  getImageUrl,
  interpolate,
};

export default config;
